import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  Calendar,
  User,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  X,
  MapPin,
  Phone,
  Mail,
  FileText
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError, showNotification } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import { realtimeService } from '../../services/realtimeService';

// Order Details Modal Component
const OrderDetailsModal = ({ order, onClose }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Format date to DD/MM/YYYY (Australian format)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    fetchOrderDetails();
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [order.id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', order.id)
        .single();

      if (error) throw error;
      setOrderDetails(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showError(error, 'Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Order Details - #{order.order_number}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : orderDetails ? (
              <div className="space-y-6">
                {/* Order Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Order Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Order Number</label>
                      <p className="mt-1 text-sm text-gray-900">{orderDetails.order_number}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Customer PO</label>
                      <p className="mt-1 text-sm text-gray-900">{orderDetails.customer_po || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <p className="mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          orderDetails.status === 'Complete' ? 'bg-green-100 text-green-800' :
                          orderDetails.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                          orderDetails.status === 'Processing' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {orderDetails.status}
                        </span>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold">
                        ${(() => {
                          if (!orderDetails.cart_items || !Array.isArray(orderDetails.cart_items)) return '0.00';
                          const total = orderDetails.cart_items.reduce((sum, item) => {
                            const price = parseFloat(item.price) || 0;
                            const quantity = parseInt(item.quantity) || 0;
                            return sum + (price * quantity);
                          }, 0);
                          return total.toFixed(2);
                        })()}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(orderDetails.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  {orderDetails.delivery_address && (
                    <div className="space-y-4">
                      <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Delivery Address</h4>
                      
                      <div className="text-sm text-gray-900">
                        <p>{orderDetails.delivery_address.address_line1}</p>
                        {orderDetails.delivery_address.address_line2 && (
                          <p>{orderDetails.delivery_address.address_line2}</p>
                        )}
                        <p>
                          {orderDetails.delivery_address.city}, {orderDetails.delivery_address.state} {orderDetails.delivery_address.postal_code}
                        </p>
                        {orderDetails.delivery_address.country && (
                          <p>{orderDetails.delivery_address.country}</p>
                        )}
                      </div>

                      {orderDetails.delivery_address.contact_name && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Contact</label>
                          <p className="mt-1 text-sm text-gray-900">{orderDetails.delivery_address.contact_name}</p>
                          {orderDetails.delivery_address.contact_phone && (
                            <p className="text-sm text-gray-600">{orderDetails.delivery_address.contact_phone}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Line Items */}
                {orderDetails.line_items && orderDetails.line_items.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">Line Items</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {orderDetails.line_items.map((item, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.part_number}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{item.description || '-'}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                ${parseFloat(item.price || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                                ${(parseFloat(item.price || 0) * parseInt(item.quantity || 0)).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Backorder Items */}
                {orderDetails.backorder_items && orderDetails.backorder_items.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">Backorder Items</h4>
                    <div className="space-y-2">
                      {orderDetails.backorder_items.map((item, index) => (
                        <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.part_number}</p>
                              <p className="text-xs text-gray-600">Quantity: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                item.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {item.status || 'pending'}
                              </span>
                              {item.expected_date && (
                                <p className="text-xs text-gray-500 mt-1">
                                  ETA: {formatDate(item.expected_date)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {orderDetails.notes && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">Notes</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{orderDetails.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Order details not available</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompanyOrders = () => {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const ordersPerPage = 20;

  // Format date to DD/MM/YYYY (Australian format)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchOrders();

      // Subscribe to realtime order updates
      const unsubscribe = realtimeService.subscribeToOrders(
        'company-orders',
        handleOrderUpdate,
        {
          events: ['INSERT', 'UPDATE', 'DELETE'],
          showNotifications: false
        }
      );

      return () => {
        unsubscribe();
      };
    }
  }, [currentUser, currentPage, searchTerm, filterStatus]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user details');
    }
  };

  const handleOrderUpdate = ({ eventType, newRecord }) => {
    console.log('[Company Orders] Order update received:', eventType);
    
    // Only refresh if the order belongs to this company
    if (currentUser && newRecord) {
      const checkOrder = async () => {
        const { data } = await supabase
          .from('accounts')
          .select('deb_id')
          .eq('id', newRecord.user_id)
          .single();
        
        if (data && data.deb_id === currentUser.deb_id) {
          fetchOrders();
        }
      };
      checkOrder();
    }
  };

  const fetchOrders = async () => {
    if (!currentUser) {
      console.log('[Company Orders] No current user');
      return;
    }

    if (!currentUser.deb_id) {
      console.error('[Company Orders] Current user has no deb_id:', currentUser);
      showError(new Error('User account missing deb_id'), 'Cannot fetch orders - missing company ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('[Company Orders] Fetching orders for company:', {
        deb_id: currentUser.deb_id,
        company_name: currentUser.company_name
      });

      // Get all company user IDs
      const { data: companyUsers, error: usersError } = await supabase
        .from('accounts')
        .select('id, full_name, email, company_name')
        .eq('deb_id', currentUser.deb_id);

      if (usersError) {
        console.error('[Company Orders] Error fetching company users:', usersError);
        throw usersError;
      }

      const userIds = companyUsers?.map(u => u.id) || [];
      console.log('[Company Orders] Found company users:', userIds.length, userIds);

      if (userIds.length === 0) {
        console.log('[Company Orders] No users found for company');
        setOrders([]);
        setFilteredOrders([]);
        setTotalOrders(0);
        setLoading(false);
        return;
      }

      // Create user details map for later use
      const userDetailsMap = {};
      companyUsers?.forEach(user => {
        userDetailsMap[user.id] = user;
      });

      // Build a simpler query - fetch all matching orders first
      let baseQuery = supabase
        .from('orders')
        .select('*')
        .in('user_id', userIds);

      // Apply status filter if set
      if (filterStatus) {
        baseQuery = baseQuery.eq('status', filterStatus);
      }

      // Order by created_at
      baseQuery = baseQuery.order('created_at', { ascending: false });

      console.log('[Company Orders] Executing query...');
      const { data: allOrdersData, error: ordersError } = await baseQuery;

      if (ordersError) {
        console.error('[Company Orders] Error fetching orders:', ordersError);
        console.error('[Company Orders] Error details:', JSON.stringify(ordersError, null, 2));
        throw ordersError;
      }

      console.log('[Company Orders] Fetched all orders:', allOrdersData?.length);

      // Apply search filter in memory if needed
      let filteredData = allOrdersData || [];
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(order =>
          order.order_number?.toLowerCase().includes(term) ||
          order.customer_po?.toLowerCase().includes(term)
        );
      }

      console.log('[Company Orders] After search filter:', filteredData.length);

      // Apply pagination in memory
      const startIndex = (currentPage - 1) * ordersPerPage;
      const endIndex = startIndex + ordersPerPage;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      // Helper function to calculate order total from cart_items
      const calculateOrderTotal = (cartItems) => {
        if (!cartItems || !Array.isArray(cartItems)) return 0;
        return cartItems.reduce((sum, item) => {
          const price = parseFloat(item.price) || 0;
          const quantity = parseInt(item.quantity) || 0;
          return sum + (price * quantity);
        }, 0);
      };

      // Attach user details and calculated total_amount to orders
      const enrichedOrders = paginatedData.map(order => ({
        ...order,
        accounts: userDetailsMap[order.user_id] || null,
        total_amount: calculateOrderTotal(order.cart_items)
      }));

      console.log('[Company Orders] Final enriched orders:', enrichedOrders.length);

      setOrders(enrichedOrders);
      setFilteredOrders(enrichedOrders);
      setTotalOrders(filteredData.length);
    } catch (error) {
      console.error('[Company Orders] Error in fetchOrders:', error);
      console.error('[Company Orders] Error message:', error.message);
      console.error('[Company Orders] Error details:', error.details);
      console.error('[Company Orders] Error hint:', error.hint);
      showError(error, 'Failed to fetch orders - check console for details');
      setOrders([]);
      setFilteredOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Complete': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      'Processing': { bg: 'bg-blue-100', text: 'text-blue-800', icon: Activity },
      'Cancelled': { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
      'pending_approval': { bg: 'bg-orange-100', text: 'text-orange-800', icon: Clock }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: Package };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const exportToCSV = () => {
    try {
      const headers = ['Order Number', 'Customer PO', 'User', 'Status', 'Amount', 'Date'];
      const csvData = filteredOrders.map(order => {
        // Calculate total from cart_items
        const total = order.cart_items && Array.isArray(order.cart_items)
          ? order.cart_items.reduce((sum, item) => {
              const price = parseFloat(item.price) || 0;
              const quantity = parseInt(item.quantity) || 0;
              return sum + (price * quantity);
            }, 0)
          : 0;
        
        return [
          order.order_number,
          order.customer_po || '',
          order.accounts?.full_name || order.accounts?.email || '',
          order.status,
          total.toFixed(2),
          formatDate(order.created_at)
        ];
      });

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `company-orders-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      showNotification('Orders exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting orders:', error);
      showError(error, 'Failed to export orders');
    }
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Company Orders"
        description={`All orders for ${currentUser?.company_name || 'your company'}`}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={exportToCSV}
              disabled={filteredOrders.length === 0}
              icon={<Download className="h-4 w-4" />}
            >
              Export CSV
            </Button>
            <Button
              variant="secondary"
              onClick={fetchOrders}
              loading={loading}
              icon={<RefreshCw className="h-4 w-4" />}
            >
              Refresh
            </Button>
          </>
        }
      />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number or PO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
            <option value="Complete">Complete</option>
            <option value="Cancelled">Cancelled</option>
            <option value="pending_approval">Pending Approval</option>
          </select>
          <div className="flex items-center text-sm text-gray-600">
            <Package className="h-4 w-4 mr-2" />
            <span>{totalOrders} total orders</span>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer PO
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        #{order.order_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {order.accounts?.full_name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {order.accounts?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.customer_po || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <DollarSign className="h-3 w-3 mr-1 text-gray-400" />
                        {parseFloat(order.total_amount || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(order.created_at)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">
                      {searchTerm || filterStatus ? 'No orders match your filters' : 'No orders found'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border border-gray-200 sm:px-6 rounded-lg shadow-sm">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * ordersPerPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * ordersPerPage, totalOrders)}
                </span>{' '}
                of <span className="font-medium">{totalOrders}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-brand border-brand text-black'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOrders;
